import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Calculator, History, Copy, Download, Settings, MoreVertical } from 'lucide-react';
import toast from 'react-hot-toast';

interface CalculatorHistory {
  id: string;
  expression: string;
  result: string;
  timestamp: Date;
}

interface Position {
  x: number;
  y: number;
}

export const FloatingCalculator = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState<Position>({ x: 20, y: 20 });
  const [display, setDisplay] = useState('0');
  const [memory, setMemory] = useState<number>(0);
  const [history, setHistory] = useState<CalculatorHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [currentOperation, setCurrentOperation] = useState<string | null>(null);
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [isNewCalculation, setIsNewCalculation] = useState(true);
  const calculatorRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);

  // Load history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('calculatorHistory');
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory).map((item: any) => ({
        ...item,
        timestamp: new Date(item.timestamp)
      })));
    }

    const savedPosition = localStorage.getItem('calculatorPosition');
    if (savedPosition) {
      setPosition(JSON.parse(savedPosition));
    }

    const savedOpacity = localStorage.getItem('calculatorOpacity');
    if (savedOpacity) {
      setOpacity(parseFloat(savedOpacity));
    }
  }, []);

  // Save history to localStorage
  useEffect(() => {
    localStorage.setItem('calculatorHistory', JSON.stringify(history));
  }, [history]);

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem('calculatorPosition', JSON.stringify(position));
  }, [position]);

  // Save opacity to localStorage
  useEffect(() => {
    localStorage.setItem('calculatorOpacity', opacity.toString());
  }, [opacity]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isVisible || showHistory) return;

      if (e.key >= '0' && e.key <= '9') {
        handleNumber(e.key);
      } else if (e.key === '.') {
        handleDecimal();
      } else if (e.key === '+' || e.key === '-' || e.key === '*' || e.key === '/') {
        handleOperation(e.key);
      } else if (e.key === 'Enter' || e.key === '=') {
        handleEquals();
      } else if (e.key === 'Escape') {
        handleClear();
      } else if (e.key === 'Backspace') {
        handleBackspace();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isVisible, showHistory, display, currentOperation, previousValue]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) { // Left click only
      setIsDragging(true);
      dragStartPos.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y
      };
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && dragStartPos.current) {
      const newX = e.clientX - dragStartPos.current.x;
      const newY = e.clientY - dragStartPos.current.y;
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    dragStartPos.current = null;
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleNumber = (num: string) => {
    if (isNewCalculation) {
      setDisplay(num);
      setIsNewCalculation(false);
    } else {
      setDisplay(display === '0' ? num : display + num);
    }
  };

  const handleDecimal = () => {
    if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const handleOperation = (op: string) => {
    if (currentOperation && !isNewCalculation) {
      handleEquals();
    }
    setPreviousValue(parseFloat(display));
    setCurrentOperation(op);
    setIsNewCalculation(true);
  };

  const handleEquals = () => {
    if (currentOperation && previousValue !== null) {
      const current = parseFloat(display);
      let result: number;

      switch (currentOperation) {
        case '+':
          result = previousValue + current;
          break;
        case '-':
          result = previousValue - current;
          break;
        case '*':
          result = previousValue * current;
          break;
        case '/':
          result = previousValue / current;
          break;
        default:
          return;
      }

      const expression = `${previousValue} ${currentOperation} ${current}`;
      const newHistory: CalculatorHistory = {
        id: Date.now().toString(),
        expression,
        result: result.toString(),
        timestamp: new Date()
      };

      setHistory(prev => [newHistory, ...prev].slice(0, 100));
      setDisplay(result.toString());
      setCurrentOperation(null);
      setPreviousValue(null);
      setIsNewCalculation(true);
    }
  };

  const handleClear = () => {
    setDisplay('0');
    setCurrentOperation(null);
    setPreviousValue(null);
    setIsNewCalculation(true);
  };

  const handleBackspace = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const handleMemoryOperation = (operation: 'M+' | 'M-' | 'MR' | 'MC') => {
    const currentValue = parseFloat(display);

    switch (operation) {
      case 'M+':
        setMemory(memory + currentValue);
        toast.success('Added to memory');
        break;
      case 'M-':
        setMemory(memory - currentValue);
        toast.success('Subtracted from memory');
        break;
      case 'MR':
        setDisplay(memory.toString());
        setIsNewCalculation(true);
        break;
      case 'MC':
        setMemory(0);
        toast.success('Memory cleared');
        break;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const exportHistory = () => {
    const historyText = history
      .map(item => `${item.timestamp.toLocaleString()}\n${item.expression} = ${item.result}`)
      .join('\n\n');

    const blob = new Blob([historyText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calculator-history-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('History exported');
  };

  const clearHistory = () => {
    setHistory([]);
    toast.success('History cleared');
  };

  if (!isVisible) {
    return (
      <motion.button
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 p-3 bg-emerald-500 text-white rounded-full shadow-lg hover:bg-emerald-600 transition-colors"
      >
        <Calculator className="w-6 h-6" />
      </motion.button>
    );
  }

  return (
    <motion.div
      ref={calculatorRef}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        opacity,
        zIndex: 9999,
      }}
      className="bg-slate-800 rounded-lg shadow-xl border border-emerald-500/20 backdrop-blur-lg"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity }}
      exit={{ scale: 0.8, opacity: 0 }}
    >
      <div
        onMouseDown={handleMouseDown}
        className="p-2 cursor-move flex items-center justify-between border-b border-emerald-500/20"
      >
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-emerald-500" />
          <span className="text-sm text-slate-300">Calculator</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="p-1 text-slate-400 hover:text-emerald-500 transition-colors"
          >
            <History className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 text-slate-400 hover:text-emerald-500 transition-colors"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="p-1 text-slate-400 hover:text-red-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            {showHistory ? (
              <div className="p-4 w-80">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">Calculation History</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={exportHistory}
                      className="p-1 text-slate-400 hover:text-emerald-500 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={clearHistory}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {history.map(item => (
                    <div
                      key={item.id}
                      className="p-2 bg-slate-700/50 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-400">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                        <button
                          onClick={() => copyToClipboard(item.result)}
                          className="p-1 text-slate-400 hover:text-emerald-500 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-300">{item.expression}</span>
                        <span className="text-emerald-500"> = {item.result}</span>
                      </div>
                    </div>
                  ))}
                  {history.length === 0 && (
                    <p className="text-center text-slate-400 py-4">
                      No calculations yet
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4 w-80">
                <input
                  type="text"
                  value={display}
                  readOnly
                  className="w-full bg-slate-700/50 text-right p-4 rounded-lg mb-4 font-mono text-xl"
                />
                <div className="grid grid-cols-4 gap-2">
                  <button
                    onClick={() => handleMemoryOperation('MC')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300"
                  >
                    MC
                  </button>
                  <button
                    onClick={() => handleMemoryOperation('MR')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300"
                  >
                    MR
                  </button>
                  <button
                    onClick={() => handleMemoryOperation('M+')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300"
                  >
                    M+
                  </button>
                  <button
                    onClick={() => handleMemoryOperation('M-')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300"
                  >
                    M-
                  </button>
                  <button
                    onClick={handleClear}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-red-500"
                  >
                    C
                  </button>
                  <button
                    onClick={handleBackspace}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-amber-500"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => handleOperation('%')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-emerald-500"
                  >
                    %
                  </button>
                  <button
                    onClick={() => handleOperation('/')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-emerald-500"
                  >
                    ÷
                  </button>
                  {['7', '8', '9'].map(num => (
                    <button
                      key={num}
                      onClick={() => handleNumber(num)}
                      className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300"
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    onClick={() => handleOperation('*')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-emerald-500"
                  >
                    ×
                  </button>
                  {['4', '5', '6'].map(num => (
                    <button
                      key={num}
                      onClick={() => handleNumber(num)}
                      className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300"
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    onClick={() => handleOperation('-')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-emerald-500"
                  >
                    -
                  </button>
                  {['1', '2', '3'].map(num => (
                    <button
                      key={num}
                      onClick={() => handleNumber(num)}
                      className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300"
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    onClick={() => handleOperation('+')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-emerald-500"
                  >
                    +
                  </button>
                  <button
                    onClick={() => handleNumber('0')}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300 col-span-2"
                  >
                    0
                  </button>
                  <button
                    onClick={handleDecimal}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors text-slate-300"
                  >
                    .
                  </button>
                  <button
                    onClick={handleEquals}
                    className="p-3 bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors text-white"
                  >
                    =
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};